export interface GraphQlError {
  message: string;
  path?: ReadonlyArray<string | number>;
}

export interface GraphQlResult<T> {
  data: T;
  errors: GraphQlError[];
}

export const parseGraphQlResponse = async <T = unknown>(
  response: Response,
): Promise<GraphQlResult<T>> => {
  if (!response.ok) throw new Error(response.statusText);

  const { data, errors } = await response.json();

  if (errors && errors.length > 0) {
    // If there is no data at all, treat as a hard failure
    if (data == null) {
      throw new Error(`Failed to fetch API: ${JSON.stringify(errors)}`);
    }
    // Partial success: field-level errors alongside valid data (GraphQL spec allows this).
    if (process.env['NODE_ENV'] === 'development') {
      console.warn(
        `[validateResponse] Partial GraphQL errors (data still returned): ${JSON.stringify(errors)}`,
      );
    }
  }

  return { data: data as T, errors: errors ?? [] };
};

export const validateResponse = async <T = unknown>(
  response: Response,
): Promise<T> => (await parseGraphQlResponse<T>(response)).data;

/**
 * True when an error belongs to `field`. An error without a path cannot be
 * ruled out, so it counts against every field.
 */
export const hasErrorForField = (
  errors: GraphQlError[],
  field: string,
): boolean =>
  errors.some((error) => !error.path?.length || error.path[0] === field);

export const describeGraphQlErrors = (errors: GraphQlError[]): string =>
  errors
    .map((error) =>
      error.path?.length
        ? `${error.message} (path: ${error.path.join('.')})`
        : `${error.message} (no path)`,
    )
    .join('; ');
